import { Link } from "react-router-dom";
import {
  ArrowRight,
  Building2,
  Trophy,
  Briefcase,
  MapPin,
  Plane,
  Sailboat,
} from "lucide-react";
import Seo from "@/components/Seo";
import { DESTINATION_IMAGES } from "@/lib/media";

const BREADCRUMB = [
  { name: "Home", path: "/" },
  { name: "Hong Kong DMC", path: "/hong-kong-dmc" },
];

const SERVICES = [
  {
    icon: Trophy,
    title: "MICE & Conferences",
    copy:
      "Full conference production at AsiaWorld-Expo, the Hong Kong Convention and Exhibition Centre (HKCEC) and Kai Tak Sports Park — from 200-pax board offsites to 5,000-pax dealer congresses.",
  },
  {
    icon: Briefcase,
    title: "Corporate Group Travel",
    copy:
      "End-to-end programme delivery — flights, hotel blocks, transfers, F&B and 24/7 ground operations across Central, Tsim Sha Tsui and Kowloon.",
  },
  {
    icon: Trophy,
    title: "Incentive Programmes",
    copy:
      "Junk-boat charters across Victoria Harbour, Michelin-starred dinners at The Peninsula, Star Ferry buyouts, and private viewing parties for the Symphony of Lights.",
  },
  {
    icon: Sailboat,
    title: "Harbour Events",
    copy:
      "Custom harbour experiences — yacht regattas, branded junk-boat parades, fireworks-themed gala dinners on rooftop venues with skyline views.",
  },
];

const VENUES = [
  { name: "Hong Kong Convention & Exhibition Centre (HKCEC)", note: "Iconic harbour-front MICE venue — 91,500 sqm" },
  { name: "AsiaWorld-Expo", note: "70,000 sqm next to HK International Airport — best for exhibitions" },
  { name: "The Peninsula Hong Kong", note: "Legendary 5-star hotel for premium corporate galas" },
  { name: "Rosewood Hong Kong", note: "Harbour-view ballrooms with rooftop access" },
  { name: "The Ritz-Carlton Hong Kong", note: "World's highest hotel — Ozone bar for incentive sundowners" },
  { name: "Kai Tak Sports Park", note: "Hong Kong's newest 50,000-seat sports & event venue" },
];

const FAQS = [
  {
    q: "Why use a DMC in Hong Kong instead of booking directly?",
    a: "Hong Kong is hyper-efficient but operationally complex. Hotel contracting, venue negotiation, transport across HK Island and Kowloon, customs for branded materials, and licensing for harbour activities all require local relationships. A DMC like Trawelldmc owns those relationships and delivers programmes at rates and quality you cannot replicate booking directly.",
  },
  {
    q: "What is the best time of year for a Hong Kong MICE event?",
    a: "October to early December and February to April are the prime windows — cool, dry weather and high event-quality. Summer months (June–August) are humid with typhoon risk, so we generally steer corporate groups away. Chinese New Year (late January / early February) sees many venues closed.",
  },
  {
    q: "Can Trawelldmc handle a combined Hong Kong + mainland China programme?",
    a: "Yes — Hong Kong is the ideal launchpad for mainland China programmes. We coordinate visa support, cross-border transfers, and partner with vetted mainland operators to deliver multi-city programmes spanning Hong Kong, Shenzhen, Guangzhou and Shanghai.",
  },
];

const FAQ_JSONLD = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: FAQS.map((f) => ({
    "@type": "Question",
    name: f.q,
    acceptedAnswer: { "@type": "Answer", text: f.a },
  })),
};

export default function HongKongDmc() {
  return (
    <div data-testid="hongkong-dmc-page">
      <Seo
        path="/hong-kong-dmc"
        title="DMC Hong Kong — Corporate & Incentive Travel | Trawelldmc"
        description="Expert DMC services in Hong Kong. Corporate groups, incentive travel, and MICE programmes. Local expertise, seamless logistics. Get a quote from Trawelldmc."
        breadcrumb={BREADCRUMB}
        extraJsonLd={FAQ_JSONLD}
      />

      <section className="relative">
        <img
          src={DESTINATION_IMAGES.hongkong}
          alt="Hong Kong corporate and incentive travel — Trawelldmc DMC"
          width="1920"
          height="1080"
          loading="eager"
          fetchPriority="high"
          decoding="async"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(90deg, rgba(11,25,44,0.92) 0%, rgba(11,25,44,0.55) 100%)",
          }}
        />
        <div className="relative max-w-7xl mx-auto px-6 md:px-10 lg:px-16 py-32 md:py-44 text-white">
          <div className="max-w-3xl">
            <div className="overline !text-[#FF5238] mb-5">
              Hong Kong DMC · Asia's Business Capital
            </div>
            <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-light tracking-tight leading-[1.06]">
              <span className="text-[#FF5238]">Hong Kong</span> Destination Management Company (DMC) & MICE Specialist
            </h1>
            <p className="text-white/80 mt-8 text-lg max-w-2xl leading-relaxed">
              Corporate, incentive and MICE programmes across Victoria Harbour
              and Kowloon — delivered by a team with two decades of Hong Kong
              experience.
            </p>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link to="/inquiry?destination=Hong%20Kong" className="btn-primary" data-testid="hk-cta-proposal">
                Get a Hong Kong proposal <ArrowRight className="h-4 w-4" />
              </Link>
              <Link to="/quote" className="btn-secondary" data-testid="hk-cta-ai-quote">
                Instant AI estimate
              </Link>
            </div>
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16 py-24 md:py-32">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <div className="overline mb-4">Why Hong Kong</div>
            <h2 className="font-heading text-4xl md:text-5xl font-light tracking-tight text-[#0B192C]">
              Asia's most concentrated <br />business travel hub.
            </h2>
          </div>
          <div className="lg:col-span-7 space-y-6 text-slate-700 text-lg leading-relaxed">
            <p>
              Hong Kong is one of the world's most efficient business travel
              destinations. Within a 30-minute radius of Central you have a
              global financial district, two of Asia's premier convention
              venues, more Michelin-starred restaurants per capita than almost
              any city on earth, and a fleet of 5-star hotels with the kind of
              harbour views that make every event photograph itself.
            </p>
            <p>
              For corporate groups, Hong Kong solves the things that slow
              programmes down elsewhere. English is widely spoken. The MTR
              metro runs on time. Hong Kong International Airport handles
              200+ destinations and is 24 minutes from Central by Airport
              Express. Visa-free access is open to passport-holders from over
              160 nationalities. And the city's compact geography means a
              4-day programme can comfortably cover Central business meetings,
              a Lantau Island offsite, and a junk-boat sunset cruise across
              Victoria Harbour without anyone burning out.
            </p>
            <p>
              The harbour is the city's superpower. From rooftop gala dinners
              at Rosewood and The Peninsula to private junk-boat charters and
              the Symphony of Lights skyline display, Hong Kong offers
              evening programming that competing destinations simply cannot
              replicate. For incentive groups, that single visual moment of
              standing on a rooftop with Victoria Harbour at sunset is the
              kind of detail that wins repeat business for years.
            </p>
            <p>
              Trawelldmc has operated Hong Kong for two decades. We own
              relationships with the city's premier MICE venues, harbour
              operators, hotel groups and Michelin restaurants. Our local
              operations team handles every contract, transfer, AV brief and
              F&B specification — so you focus on your delegates, not the
              logistics.
            </p>
          </div>
        </div>
      </section>

      <section className="bg-[#FDFCF8] border-y border-slate-200">
        <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16 py-24 md:py-32">
          <div className="mb-16 max-w-2xl">
            <div className="overline mb-4">What we deliver</div>
            <h2 className="font-heading text-4xl md:text-5xl font-light tracking-tight text-[#0B192C]">
              Our Hong Kong DMC services.
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {SERVICES.map((s) => (
              <div key={s.title} className="card-flat p-8">
                <s.icon className="h-8 w-8 text-[#FF5238]" />
                <h3 className="font-heading text-xl font-medium tracking-tight text-[#0B192C] mt-5">
                  {s.title}
                </h3>
                <p className="text-slate-600 mt-3 leading-relaxed">{s.copy}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-[#0B192C] text-white">
        <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16 py-24 md:py-32">
          <div className="mb-16 max-w-2xl">
            <div className="overline !text-[#FF5238] mb-4">Top venues</div>
            <h2 className="font-heading text-4xl md:text-5xl font-light tracking-tight">
              Venues we work <br />
              <span className="text-[#FF5238]">across the harbour.</span>
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-6">
            {VENUES.map((v) => (
              <div key={v.name} className="border-t border-white/15 pt-5">
                <div className="flex gap-3 items-start">
                  <Building2 className="h-5 w-5 text-[#FF5238] flex-shrink-0 mt-1" />
                  <div>
                    <div className="font-heading text-lg font-medium">{v.name}</div>
                    <div className="text-sm text-white/60 mt-1">{v.note}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16 py-24 md:py-32">
        <div className="mb-16 max-w-2xl">
          <div className="overline mb-4">FAQ</div>
          <h2 className="font-heading text-4xl md:text-5xl font-light tracking-tight text-[#0B192C]">
            Frequently Asked Questions
          </h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-10">
          {FAQS.map((f, i) => (
            <div key={f.q} data-testid={`hk-faq-${i}`}>
              <h3 className="font-heading text-xl font-medium text-[#0B192C] tracking-tight">{f.q}</h3>
              <p className="text-slate-600 mt-3 leading-relaxed">{f.a}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="bg-[#FDFCF8] border-y border-slate-200">
        <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16 py-24 md:py-32 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <div className="overline mb-4">Next step</div>
            <h2 className="font-heading text-4xl md:text-5xl font-light tracking-tight text-[#0B192C]">
              Interested in <span className="text-[#FF5238]">Hong Kong?</span>
            </h2>
            <p className="text-slate-600 mt-6 max-w-xl text-lg leading-relaxed">
              Let Trawelldmc handle everything. Request a proposal today —
              our team responds within 24 hours.
            </p>
          </div>
          <div className="lg:col-span-5 flex flex-col gap-4">
            <Link to="/inquiry?destination=Hong%20Kong" className="btn-primary w-full justify-center" data-testid="hk-bottom-cta">
              Request a proposal <ArrowRight className="h-4 w-4" />
            </Link>
            <Link to="/contact" className="btn-secondary w-full justify-center">Contact our team</Link>
          </div>
        </div>
      </section>

      <section>
        <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16 py-16">
          <div className="overline mb-6">You may also like</div>
          <nav className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6" aria-label="Related DMC destination pages">
            <Link to="/dubai-dmc" className="group flex items-start gap-3" data-testid="hk-link-dubai">
              <MapPin className="h-5 w-5 text-[#FF5238] mt-0.5" />
              <div>
                <div className="font-medium text-[#0B192C] group-hover:underline">DMC Dubai</div>
                <div className="text-sm text-slate-500 mt-1">GCC HQ · MICE & corporate</div>
              </div>
            </Link>
            <Link to="/singapore-dmc" className="group flex items-start gap-3" data-testid="hk-link-singapore">
              <MapPin className="h-5 w-5 text-[#FF5238] mt-0.5" />
              <div>
                <div className="font-medium text-[#0B192C] group-hover:underline">DMC Singapore</div>
                <div className="text-sm text-slate-500 mt-1">MICE capital of Asia</div>
              </div>
            </Link>
            <Link to="/vietnam-dmc" className="group flex items-start gap-3" data-testid="hk-link-vietnam">
              <MapPin className="h-5 w-5 text-[#FF5238] mt-0.5" />
              <div>
                <div className="font-medium text-[#0B192C] group-hover:underline">DMC Vietnam</div>
                <div className="text-sm text-slate-500 mt-1">Hanoi · HCMC · Ha Long Bay</div>
              </div>
            </Link>
            <Link to="/" className="group flex items-start gap-3" data-testid="hk-link-home">
              <Plane className="h-5 w-5 text-[#FF5238] mt-0.5" />
              <div>
                <div className="font-medium text-[#0B192C] group-hover:underline">Trawelldmc Home</div>
                <div className="text-sm text-slate-500 mt-1">All destinations & services</div>
              </div>
            </Link>
          </nav>
        </div>
      </section>
    </div>
  );
}
